import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    ViewEncapsulation,
    booleanAttribute,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';

import { uniqueId } from 'lodash-es';

import { DsTooltipTrigger, OVERLAY_REF_WITH_DATA_TOKEN } from './tooltip.directives';

export const DS_TOOLTIP_VARIANT_ARRAY = ['neutral', 'utility'] as const;
export type DsTooltipVariant = (typeof DS_TOOLTIP_VARIANT_ARRAY)[number];

@Component({
    selector: 'ds-tooltip-content',
    templateUrl: './tooltip.component.html',
    styleUrls: ['./tooltip.component.scss'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.customTooltip]': 'customTooltip()',
        '(click)': 'onActionClick($event)',
        '(keydown)': 'handleKeydown($event)',
        '(focusout)': 'handleFocusOut($event)',
        '[attr.role]': 'role()',
        '[attr.aria-labelledby]': 'role() === "dialog" ? (tooltipId() + "-title") : null',
    },
})
export class DsTooltipContent implements AfterViewInit {
    private readonly _tooltipTrigger = inject(DsTooltipTrigger);
    private readonly _elementRef = inject(ElementRef);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _lastKeyWasTab = signal(false);

    protected readonly id = uniqueId('ds-tooltip-');

    protected readonly overlay = inject(OVERLAY_REF_WITH_DATA_TOKEN);

    public readonly inverse = input(false, { transform: booleanAttribute });
    public readonly variant = input<DsTooltipVariant>('neutral');
    public readonly fullWidth = input(false, { transform: booleanAttribute });
    public readonly fullWidthMode = input<'fullwidth' | 'fullwidthMargin'>('fullwidthMargin');
    public readonly customTooltip = input(false, { transform: booleanAttribute });
    public readonly interactive = computed(() => this._tooltipTrigger.interactive());
    public readonly tooltipTitle = input<string>('');

    protected readonly outerContainerClasses = computed(() => {
        const positionClass = `ds-tooltip-${this.overlay.data.position}`;
        const arrowClass = `ds-tooltip-arrow-${this.overlay.data.arrowPosition}`;
        const fullWidthClass = this.fullWidth() ? 'ds-tooltip-full-width' : '';
        const inverse = this.inverse() ? 'ds-tooltip-inverse' : '';
        const fullWidthModeClass = this.fullWidthMode() === 'fullwidthMargin' ? 'ds-tooltip-fullwidth-margin' : '';
        const adjustedArrowClass =
            this.overlay.data.position === 'left' || this.overlay.data.position === 'right' ? `ds-tooltip-arrow-middle` : arrowClass; // Adjust arrow for side positions

        return `ds-tooltip-outer-container ds-tooltip-${this.variant()} ${positionClass} ${adjustedArrowClass} ${fullWidthClass} ${fullWidthModeClass} ${inverse}`;
    });

    public readonly closed = output<{ type: string; source: string }>();
    public readonly role = computed(() => (this.interactive() ? 'dialog' : 'tooltip'));

    ngAfterViewInit(): void {
        this.focusFirstElement();
    }

    private focusFirstElement(): void {
        if (this.interactive() && this._tooltipTrigger.isKeyboardFocused()) {
            const firstElement = this._elementRef.nativeElement.querySelector('button:not([disabled]), a:not([tabindex="-1"])') as HTMLElement;

            if (firstElement) {
                setTimeout(() => {
                    firstElement.focus();
                }, 0);
            }
        }
    }

    public handleFocusOut(event: FocusEvent): void {
        if (!this.interactive()) {
            this._lastKeyWasTab.set(false);
            return;
        }

        const relatedTarget = event.relatedTarget as HTMLElement;
        const isLeavingTooltip = !relatedTarget || !this._elementRef.nativeElement.contains(relatedTarget);

        const isGoingToTrigger = relatedTarget && this._tooltipTrigger.elementRef.nativeElement.contains(relatedTarget);

        if (isLeavingTooltip && !isGoingToTrigger && this._lastKeyWasTab()) {
            const timeoutId = setTimeout(() => {
                this.closeTooltip(event);
            }, 0);

            this._destroyRef.onDestroy(() => clearTimeout(timeoutId));
        }

        this._lastKeyWasTab.set(false);
    }

    onActionClick(event: Event): void {
        const target = event.target as HTMLElement;

        // Look for an element with the custom attribute (e.g. dsTooltipClose)
        const actionClose = target.closest('[dsTooltipClose]');
        if (actionClose) {
            this.closeTooltip(event);
        }
    }

    public closeTooltip(event: Event): void {
        if (this.overlay) {
            event.stopPropagation();
            this.closed.emit({ type: event.type, source: 'close' });
            this._tooltipTrigger.tooltipToggleChange.emit({ isTooltipOpen: false, source: 'close' });

            if (this.interactive()) {
                setTimeout(() => {
                    this._tooltipTrigger.elementRef.nativeElement?.focus();
                }, 0);
            }

            this.overlay.ref.detach();
        }
    }

    // Handle keydown events for accessibility
    public handleKeydown(event: KeyboardEvent): void {
        this._lastKeyWasTab.set(event.key === 'Tab');

        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeTooltip(event);
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            const target = event.target as HTMLElement;
            const actionClose = target.closest('[dsTooltipClose]');
            if (actionClose) {
                event.preventDefault();
                this.closeTooltip(event);
            }
        }
    }

    public tooltipId(): string {
        return this.id;
    }
}
